import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';

import { ModalService } from '../../services';
import { DeleteModalComponent } from '../modals';
import { FileData } from '../../models';

@Component({
  selector: 'app-file-upload',
  styleUrl: 'file-upload.component.scss',
  templateUrl: 'file-upload.component.html',
})
export class FileUploadComponent {
  @ViewChild('input') input: HTMLInputElement;

  @Input() width = 200;
  @Input() height = 150;
  @Input() header: string;
  @Input() largeHeader = true;
  @Input() src: string;
  @Input() showPreview = false;
  @Input() resizeImage = false;
  @Input() showBrowse = true;
  @Input() showRemove = true;
  @Input() browseButtonText = $localize`Browse`;
  @Input() image = false;
  @Output() save = new EventEmitter<FileData>();
  @Output() remove = new EventEmitter<string>();

  public file: FileData = {} as FileData;
  public showUpload: boolean;

  constructor(private modalService: ModalService) {}

  get imageSource(): string {
    return this.src || this.file.src;
  }

  onUpload() {
    this.showUpload = false;
    this.save.emit(this.file);
  }

  onRemove() {
    if (this.src) {
      this.modalService.openSmall(DeleteModalComponent).modal.result.then(
        () => {
          if (this.input) {
            this.input.value = '';
          }

          if (this.file.src) {
            this.file.src = undefined;
            if (this.input) {
              this.input.value = '';
            }
          }

          this.remove.emit();
        },
        () => {},
      );
    }
  }

  // This is called when the user selects new files from the browse button
  fileChange(input: HTMLInputElement) {
    this.readFiles(input.files[0], () => {
      if (this.showPreview) {
        this.showUpload = true;
      } else {
        this.save.emit(this.file);
      }
    });
  }

  private readFiles(file: File, callback: any) {
    // Create the file reader
    const reader = new FileReader();

    // If there is a file
    // Start reading this file
    this.readFile(file, reader, (result: any) => {
      if (this.image) {
        // Create an img element and add the image file data to it
        const img = document.createElement('img');
        img.src = result;

        // Send this img to the resize function (and wait for callback)
        this.resize(img, (resized_jpeg: any, before: any, after: any) => {
          // Add the resized jpeg img source to a list for preview
          // This is also the file you want to upload. (either as a
          // base64 string or img.src = resized_jpeg if you prefer a file).
          this.file.name = file.name;
          this.file.src = resized_jpeg;
          callback();
        });
      } else {
        this.file.name = file.name;
        this.file.src = result;
        callback();
      }
    });
  }

  private readFile(file: File, reader: any, callback: any) {
    // Set a callback function to fire after the file is fully loaded
    reader.onload = () => callback(reader.result);

    // Read the file
    reader.readAsDataURL(file);
  }

  private resize(img: any, callback: any) {
    // This will wait until the img is loaded before calling this function
    return (img.onload = () => {
      // Get the images current width and height
      const qualityMultiplier = 5;
      let width = img.width * qualityMultiplier;
      let height = img.height * qualityMultiplier;
      const maxWidth = this.width * qualityMultiplier;
      const maxHeight = this.height * qualityMultiplier;

      // Set the WxH to fit the Max values (but maintain proportions)
      if (this.resize) {
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
      }

      // create a canvas object
      const canvas = document.createElement('canvas');

      // Set the canvas to the new calculated dimensions
      canvas.width = width;
      canvas.height = height;
      const ctx: any = canvas.getContext('2d');

      ctx.drawImage(img, 0, 0, width, height);

      // Get this encoded as a jpeg
      // IMPORTANT: 'jpeg' NOT 'jpg'
      const dataUrl = canvas.toDataURL('image/jpeg');

      // callback with the results
      callback(dataUrl, img.src.length, dataUrl.length);
    });
  }

  openPreview() {
    window.open(this.imageSource);
  }
}
